import type { OttVideo } from "@/lib/lecturehub/types";
import { videoStreamUrl } from "@/lib/lecturehub/video";

/**
 * Series episodes — the pure half of N01 (decision D32).
 *
 * What an episode can do for THIS caller is read from the episode's own data,
 * never from the parent module's `canView`: the backend fills a video's stream
 * per FILE, OR across every module the file is attached to (09-gotchas rule 3),
 * so a locked series can carry playable episodes and a viewable one an episode
 * still transcoding. Verified on dev 2026-09-13: module 457 (PREMIUM, guest)
 * has 10 of 12 episode streams filled, module 458 (REGISTERED, guest) 2 of 3.
 */
export type EpisodeState = "playable" | "processing" | "locked" | "unavailable";

/** The URL parameter that names the open episode: `?e=<OttVideo.id>`. */
export const EPISODE_PARAM = "e";

export function episodeState(v: OttVideo | null | undefined): EpisodeState {
  const video = v?.video;
  if (!video) return "unavailable";
  if (video.processed === false) return "processing";
  return videoStreamUrl(video) ? "playable" : "locked";
}

export function firstPlayableIndex(videos: readonly OttVideo[]): number {
  return videos.findIndex((v) => episodeState(v) === "playable");
}

/** The next playable episode after `from`, or -1: a series ends, it does not wrap. */
export function nextPlayableIndex(videos: readonly OttVideo[], from: number): number {
  for (let i = Math.max(-1, from) + 1; i < videos.length; i++) {
    if (episodeState(videos[i]) === "playable") return i;
  }
  return -1;
}

/**
 * Which episode the stage opens on: the one the URL names when it exists, else
 * the first playable one, else the first episode — so a fully locked series
 * still shows episode 1's poster behind the gate. -1 when there are none.
 */
export function initialEpisodeIndex(
  videos: readonly OttVideo[],
  requestedId: string | null | undefined,
): number {
  if (videos.length === 0) return -1;
  if (requestedId) {
    const i = videos.findIndex((v) => String(v.id) === String(requestedId));
    if (i >= 0) return i;
  }
  const playable = firstPlayableIndex(videos);
  return playable >= 0 ? playable : 0;
}
